// Kudos + Suggestions + Daily Checklists pages
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, initials } from "@/lib/utils";
import { plainError } from "@/lib/plain-error";
import {
  suggestionInbox, STATUS_LABEL,
  type SuggestionFacts, type SuggestionStatus,
} from "@/lib/culture/suggestion-loop";
import {
  decisionVerdict, type Decision, type DecidableSuggestion,
} from "@/lib/culture/suggestion-decision";

const EMOJIS = ["🎉", "🙌", "🚀", "🔥", "🏆", "⭐", "💪", "🌟", "👏", "💯"];

interface Kudo { id: string; from_employee_id: string; to_employee_id: string; message: string; emoji: string; created_at: string; }
interface Suggestion extends SuggestionFacts { content: string; response_published_at?: string | null }

/** What a job produced, confirmed with the provider rather than with our own row. */
interface WorkCard {
  stage: "WORKING" | "NEEDS_JEREMY" | "READY" | "SHIPPED" | "SUPERSEDED"
    | "FAILED" | "ALREADY_DONE" | "MERGED";
  headline: string;
  detail?: string | null;
  summary?: string | null;
  retry?: boolean;
  pull_request_url?: string;
  pull_request_number?: number;
  branch?: string | null;
  files_changed?: number | null;
  additions?: number | null;
  deletions?: number | null;
  pr_state?: string;
  checks?: string[];
}
interface Person { id: string; first_name: string | null; last_name: string | null; email: string; profile_picture_path: string | null; }

export function Kudos() {
  const { employee } = useAuth();
  const [people, setPeople] = useState<Person[]>([]);
  const [feed, setFeed] = useState<Kudo[]>([]);
  const [toId, setToId] = useState("");
  const [msg, setMsg] = useState("");
  const [emoji, setEmoji] = useState("🎉");
  const [busy, setBusy] = useState(false); const [error, setError] = useState<string | null>(null);

  useEffect(() => { void load(); }, []);
  useEffect(() => {
    const ch = supabase.channel("kudos-feed").on("postgres_changes", { event: "INSERT", schema: "public", table: "hq_kudos" }, payload => setFeed(prev => [payload.new as Kudo, ...prev])).subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, []);

  async function load() {
    const [{ data: p }, { data: k }] = await Promise.all([
      // hq_directory is the roster view every employee can read (hq_employees
      // itself is admin-only via RLS, which left this dropdown empty for staff).
      supabase.from("hq_directory").select("id,first_name,last_name,email,profile_picture_path").order("first_name"),
      supabase.from("hq_kudos").select("*").order("created_at", { ascending: false }).limit(50),
    ]);
    setPeople(((p as Person[]) ?? []).filter(x => x.id !== employee?.id));
    setFeed((k as Kudo[]) ?? []);
  }

  async function send(e: React.FormEvent) {
    e.preventDefault();
    if (!employee || !toId || !msg.trim()) return;
    setBusy(true); setError(null);
    const { data, error } = await supabase.from("hq_kudos").insert({ from_employee_id: employee.id, to_employee_id: toId, message: msg.trim(), emoji }).select("id").single();
    setBusy(false);
    if (error) { setError(error.message); return; }
    setMsg(""); setToId("");
    // Email the recipient too (in-app they also get the company-wide bell alert).
    // Fire-and-forget — a mail hiccup shouldn't block sending a kudos.
    const kudosId = (data as { id: string } | null)?.id;
    if (kudosId) {
      void supabase.auth.getSession().then(({ data: s }) =>
        fetch("/.netlify/functions/kudos-email", {
          method: "POST",
          headers: { Authorization: `Bearer ${s.session?.access_token}`, "Content-Type": "application/json" },
          body: JSON.stringify({ kudos_id: kudosId }),
        }).catch(() => {}));
    }
  }

  const empById = useMemo(() => {
    const all = [employee, ...people].filter(Boolean) as Person[];
    return Object.fromEntries(all.map(e => [e.id, e]));
  }, [people, employee]);

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">Kudos 🎉</h1>
      <p className="text-sm text-gray2">Send a shoutout to a teammate. The whole company gets an alert, and the recipient gets a "You've Been Scented" notification — plus an email so it reaches them even off HQ.</p>

      <form onSubmit={send} className="card space-y-3">
        {error && <div className="rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">{error}</div>}
        <div className="grid gap-3 sm:grid-cols-[1fr_120px]">
          <div><label className="label">To</label><select required className="input" value={toId} onChange={e => setToId(e.target.value)}><option value="">— Select a teammate —</option>{people.map(p => <option key={p.id} value={p.id}>{p.first_name ?? p.email} {p.last_name ?? ""}</option>)}</select></div>
          <div><label className="label">Emoji</label><div className="flex flex-wrap gap-1">{EMOJIS.map(e => <button key={e} type="button" onClick={() => setEmoji(e)} className={`text-xl p-1 rounded ${emoji === e ? "bg-teal-mint" : "hover:bg-offwhite"}`}>{e}</button>)}</div></div>
        </div>
        <div><label className="label">Message *</label><textarea required rows={2} className="input" value={msg} onChange={e => setMsg(e.target.value)} placeholder="What did they do that was awesome?" /></div>
        <div className="flex justify-end"><button className="btn" disabled={busy || !toId || !msg.trim()}>Send Kudos</button></div>
      </form>

      <div className="space-y-3">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-gray2">Recent shoutouts</h2>
        {feed.length === 0 ? <p className="card py-8 text-center text-sm text-gray2">No kudos yet. Send the first one!</p> : feed.map(k => {
          const from = empById[k.from_employee_id]; const to = empById[k.to_employee_id];
          return (
            <div key={k.id} className="card">
              <div className="flex items-start gap-3">
                <span className="text-2xl">{k.emoji}</span>
                <div className="flex-1">
                  <div className="text-sm">
                    <strong>{from?.first_name ?? "Someone"}</strong>{" → "}<strong>{to?.first_name ?? "someone"}</strong>
                  </div>
                  <div className="mt-1">{k.message}</div>
                  <div className="mt-1 text-xs text-gray2">{fmtDate(k.created_at)}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function Suggestions() {
  const { employee } = useAuth();
  const [content, setContent] = useState(""); const [category, setCategory] = useState("");
  const [anon, setAnon] = useState(true);
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<string | null>(null); const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!content.trim()) return;
    setBusy(true); setError(null); setInfo(null);
    const { error } = await supabase.from("hq_suggestions").insert({
      author_id: anon ? null : employee?.id,
      is_anonymous: anon,
      category: category || null,
      content: content.trim(),
    });
    setBusy(false);
    if (error) setError(error.message);
    else { setInfo("✓ Suggestion submitted. Thank you!"); setContent(""); setCategory(""); }
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <h1 className="text-2xl font-bold tracking-tight">Suggestion Box 📥</h1>
      <p className="text-sm text-gray2">Got an idea to make Scenta better? Drop it here. You can submit anonymously — only admins see suggestions.</p>
      {info && <div className="rounded-lg bg-ok-bg px-3.5 py-2.5 text-sm text-ok">{info}</div>}
      {error && <div className="rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">{error}</div>}
      <form onSubmit={submit} className="card space-y-4">
        <div><label className="label">Category</label><select className="input" value={category} onChange={e => setCategory(e.target.value)}><option value="">—</option>{["Process","Product","Culture","Tools","Other"].map(c => <option key={c}>{c}</option>)}</select></div>
        <div><label className="label">Your suggestion *</label><textarea required rows={5} className="input" value={content} onChange={e => setContent(e.target.value)} /></div>
        <label className="flex items-center gap-2 text-sm rounded-lg bg-offwhite px-3 py-2"><input type="checkbox" checked={anon} onChange={e => setAnon(e.target.checked)} /> Submit anonymously</label>
        <div className="flex justify-end"><button className="btn" disabled={busy || !content.trim()}>Submit</button></div>
      </form>
      <MySuggestions key={info ?? "list"} />
      <AnsweredSuggestions />
    </div>
  );
}

interface ThreadMessage {
  id: string; suggestion_id: string; author_id: string;
  from_admin: boolean; body: string; created_at: string;
}
interface OwnSuggestion {
  id: string; category: string | null; content: string; status: string;
  admin_response: string | null; created_at: string;
}

/**
 * What happened to the suggestions you put your name to.
 *
 * HQ-SUGGEST-001. There was no such surface. /suggestions read only
 * hq_suggestions_public, and a named suggestion is deliberately absent from
 * that view — publicSummaryFor() returns null for one, because a named
 * submission is not republished verbatim. So an administrator could ask the
 * author a question and the author had nowhere to read it and no way to reply;
 * the only move available was submitting a second suggestion, which is how a
 * feedback channel teaches people to stop using it.
 *
 * The row was always readable by its author — hq_suggestions_read_own has
 * existed since 2026-10-17. Nothing asked for it.
 *
 * Anonymous submissions have no author_id and never appear here. That is what
 * anonymity costs, and the empty-state says so rather than looking broken.
 */
function MySuggestions() {
  const { employee } = useAuth();
  const [rows, setRows] = useState<OwnSuggestion[]>([]);
  const [thread, setThread] = useState<Record<string, ThreadMessage[]>>({});
  const [reply, setReply] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { if (employee) void load(); }, [employee?.id]);

  async function load() {
    if (!employee) return;
    const { data, error } = await supabase.from("hq_suggestions")
      .select("id,category,content,status,admin_response,created_at")
      .eq("author_id", employee.id)
      .order("created_at", { ascending: false });
    if (error) { setErr(plainError(error, "load your suggestions")); return; }
    const list = (data as OwnSuggestion[]) ?? [];
    setRows(list);
    if (list.length === 0) return;

    const { data: msgs } = await supabase.from("hq_suggestion_messages")
      .select("*").in("suggestion_id", list.map(r => r.id))
      .order("created_at");
    const byId: Record<string, ThreadMessage[]> = {};
    for (const m of ((msgs as ThreadMessage[]) ?? [])) (byId[m.suggestion_id] ||= []).push(m);
    setThread(byId);
  }

  async function send(suggestionId: string) {
    const body = (reply[suggestionId] ?? "").trim();
    if (!body || !employee) return;
    setBusy(suggestionId); setErr(null);
    const { error } = await supabase.from("hq_suggestion_messages").insert({
      suggestion_id: suggestionId, author_id: employee.id, from_admin: false, body,
    });
    setBusy(null);
    if (error) { setErr(plainError(error, "send that reply")); return; }
    setReply(r => ({ ...r, [suggestionId]: "" }));
    await load();
  }

  if (rows.length === 0) return null;

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold">Yours</h2>
      <p className="text-sm text-gray2">
        Suggestions you put your name to, and anything we have asked you about. Anonymous ones are not
        listed here — there is no way to tie them back to you, which is the point of them.
      </p>
      {err && <div className="rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">{err}</div>}
      {rows.map(r => {
        const msgs = thread[r.id] ?? [];
        const asked = r.status === "awaiting_author";
        return (
          <div key={r.id} className={`card ${asked ? "ring-1 ring-warn" : ""}`}>
            <div className="mb-1 flex flex-wrap items-center gap-2">
              {r.category && <span className="pill pill-mint">{r.category}</span>}
              <span className={`pill ${asked ? "pill-warn" : ""}`}>
                {STATUS_LABEL[r.status as SuggestionStatus] ?? r.status}
              </span>
              <span className="text-xs text-gray2">{fmtDate(r.created_at)}</span>
            </div>
            <p className="whitespace-pre-wrap text-sm">{r.content}</p>

            {r.admin_response && (
              <div className="mt-2 rounded-lg bg-offwhite px-3 py-2 text-sm">
                <div className="text-xs font-semibold uppercase tracking-wider text-gray2">Answer</div>
                <p className="mt-0.5 whitespace-pre-wrap">{r.admin_response}</p>
              </div>
            )}

            {msgs.length > 0 && (
              <div className="mt-3 space-y-2 border-t border-line pt-3">
                {msgs.map(m => (
                  <div key={m.id} className={m.from_admin ? "" : "pl-6"}>
                    <div className="text-xs text-gray2">
                      {m.from_admin ? "Asked" : "You replied"} · {fmtDate(m.created_at)}
                    </div>
                    <p className="whitespace-pre-wrap text-sm">{m.body}</p>
                  </div>
                ))}
              </div>
            )}

            {msgs.length > 0 && (
              <div className="mt-3 flex gap-2">
                <input
                  className="input flex-1"
                  placeholder={asked ? "Answer the question…" : "Add to this…"}
                  value={reply[r.id] ?? ""}
                  onChange={e => setReply(x => ({ ...x, [r.id]: e.target.value }))}
                  onKeyDown={e => { if (e.key === "Enter") void send(r.id); }}
                  aria-label="Your reply"
                />
                <button
                  onClick={() => void send(r.id)}
                  disabled={busy === r.id || !(reply[r.id] ?? "").trim()}
                  className="btn"
                >{busy === r.id ? "Sending…" : "Reply"}</button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

interface PublicSuggestion { id: string; category: string | null; summary: string | null; status: string; admin_response: string | null; response_published_at: string | null; }

/**
 * What has actually been answered.
 *
 * The point of the whole box. Without this the submit form is a request for
 * effort with no evidence that any of it ever went anywhere, and people stop
 * using it — then stop believing the next channel too.
 */
function AnsweredSuggestions() {
  const [rows, setRows] = useState<PublicSuggestion[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => { void (async () => {
    const { data, error } = await supabase
      .from("hq_suggestions_public").select("*")
      .order("response_published_at", { ascending: false }).limit(20);
    // The view arrives with 2026-10-17_suggestion_loop.sql. Until it is applied
    // this section is absent rather than showing an error to every employee.
    if (!error) { setRows((data as PublicSuggestion[]) ?? []); setReady(true); }
  })(); }, []);

  if (!ready || rows.length === 0) return null;

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold">Answered</h2>
      <p className="text-sm text-gray2">
        Suggestions people made, and what happened. Anonymous ones are shown as a restatement of the ask,
        never the original wording.
      </p>
      {rows.map(r => (
        <div key={r.id} className="card">
          <div className="mb-1 flex items-center gap-2">
            {r.category && <span className="pill pill-mint">{r.category}</span>}
            <span className="pill">{STATUS_LABEL[r.status as SuggestionStatus] ?? r.status}</span>
            {r.response_published_at && <span className="text-xs text-gray2">{fmtDate(r.response_published_at)}</span>}
          </div>
          <p className="text-sm font-medium">{r.summary}</p>
          {r.admin_response && <p className="mt-1 whitespace-pre-wrap text-sm text-gray2">{r.admin_response}</p>}
        </div>
      ))}
    </div>
  );
}

/**
 * What Jeremy decides about a suggestion.
 *
 * ── WHAT THIS SCREEN USED TO ASK OF HIM ────────────────────────────────────
 * Four status buttons, a written answer, a publish button, a work title, an
 * engineering brief, a list of the files an engineer may touch, a queue button
 * and two approve buttons. Eleven controls to answer one question, and most of
 * them existed because something downstream needed a value.
 *
 * He answers the question. HQ does the rest: writes the answer, delivers it,
 * creates the work and starts Cole looking. What files are involved is worked
 * out by the engineer, from the code, which is the only place that answer has
 * ever lived.
 */
export function SuggestionsAdmin() {
  const [rows, setRows] = useState<Suggestion[]>([]);
  const [work, setWork] = useState<Record<string, WorkCard>>({});
  const [openInput, setOpenInput] = useState<{ id: string; decision: Decision | "clarify" } | null>(null);
  const [text, setText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => { void load(); }, []);
  async function load() {
    const { data } = await supabase.from("hq_suggestions")
      .select("*").order("created_at", { ascending: false });
    setRows((data as Suggestion[]) ?? []);

    // ── WHY THIS IS NOT READ STRAIGHT FROM THE TABLE ────────────────────
    // It was, and the card turned our own status column into "Cole finished
    // this" with nothing to look at. What a job actually produced is confirmed
    // against GitHub on the server, because a status column is HQ agreeing
    // with itself.
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/.netlify/functions/hq-work-status", {
      method: "POST",
      headers: { Authorization: `Bearer ${session?.access_token ?? ""}`, "Content-Type": "application/json" },
      body: "{}",
    });
    const out = await res.json().catch(() => ({}));
    setWork(out.work ?? {});
  }

  const [adminThread, setAdminThread] = useState<Record<string, ThreadMessage[]>>({});

  // The conversation on each suggestion, so the card shows what was asked and
  // what came back rather than only the original submission.
  useEffect(() => { void (async () => {
    if (rows.length === 0) return;
    const { data } = await supabase.from("hq_suggestion_messages")
      .select("*").in("suggestion_id", rows.map(r => r.id)).order("created_at");
    const byThread: Record<string, ThreadMessage[]> = {};
    for (const m of ((data as ThreadMessage[]) ?? [])) (byThread[m.suggestion_id] ||= []).push(m);
    setAdminThread(byThread);
  })(); }, [rows]);

  const inbox = useMemo(() => suggestionInbox(rows, Date.now()), [rows]);
  const byId = useMemo(() => new Map(rows.map(r => [r.id, r])), [rows]);

  async function decide(r: Suggestion, decision: Decision, body: string) {
    const facts: DecidableSuggestion = {
      id: r.id, status: r.status, content: r.content,
      category: r.category ?? null, is_anonymous: r.is_anonymous, author_id: r.author_id,
    };
    const verdict = decisionVerdict(facts, decision, body);
    if (!verdict.allowed) { setError(verdict.reason); return; }

    setBusyId(r.id); setError(null);
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/.netlify/functions/hq-suggestion-decide", {
      method: "POST",
      headers: { Authorization: `Bearer ${session?.access_token ?? ""}`, "Content-Type": "application/json" },
      body: JSON.stringify({ suggestion_id: r.id, decision, text: body }),
    });
    const out = await res.json().catch(() => ({}));
    setBusyId(null);
    if (!res.ok) { setError(out.message ?? "That did not save."); return; }
    setOpenInput(null); setText("");
    if (out.note) setError(out.note);
    await load();
  }

  // ── "THAT'S NOT WHAT THEY MEANT" ──────────────────────────────────────
  // Cole's conclusion is often right for the WORDS and wrong for the intent —
  // "emoji already send fine" was true, and the asker meant a picker. This
  // sends the corrected intent back: a new run whose brief carries the original
  // ask, his earlier conclusion, and the clarification marked authoritative.
  async function clarify(r: Suggestion, feedback: string) {
    setBusyId(r.id); setError(null);
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/.netlify/functions/hq-work-dispatch", {
      method: "POST",
      headers: { Authorization: `Bearer ${session?.access_token ?? ""}`, "Content-Type": "application/json" },
      body: JSON.stringify({ kind: "suggestion", id: r.id, clarify: feedback }),
    });
    const out = await res.json().catch(() => ({}));
    setBusyId(null);
    if (!res.ok) { setError(out.message ?? "That could not be sent."); return; }
    setOpenInput(null); setText("");
    await load();
  }

  async function resume(r: Suggestion, answer: string) {
    setBusyId(r.id); setError(null);
    const { data: { session } } = await supabase.auth.getSession();
    const res = await fetch("/.netlify/functions/hq-suggestion-decide", {
      method: "POST",
      headers: { Authorization: `Bearer ${session?.access_token ?? ""}`, "Content-Type": "application/json" },
      body: JSON.stringify({ suggestion_id: r.id, decision: "resume", text: answer }),
    });
    const out = await res.json().catch(() => ({}));
    setBusyId(null);
    if (!res.ok) { setError(out.message ?? "That could not be sent."); return; }
    setOpenInput(null); setText("");
    await load();
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Suggestions</h1>
        <p className="mt-1 text-sm text-gray2">{inbox.headline}</p>
      </div>

      {error && <div className="rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">{error}</div>}

      <div className="space-y-3">
        {inbox.rows.map(loop => {
          const r = byId.get(loop.id)!;
          const decided = ["planned", "implemented", "wont_do"].includes(r.status);
          const w = work[r.id];
          const asking = openInput?.id === r.id;
          return (
            <div key={r.id} className="card">
              <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
                <span className="text-xs text-gray2">
                  {r.category ? `${r.category} · ` : ""}{fmtDate(r.created_at)}
                  {r.is_anonymous ? " · sent anonymously" : ""}
                </span>
                {decided && (
                  <span className={`pill ${r.status === "wont_do" ? "" : "pill-mint"}`}>
                    {r.status === "wont_do" ? "Declined" : r.status === "implemented" ? "Done" : "Approved"}
                  </span>
                )}
              </div>

              <p className="whitespace-pre-wrap text-sm">{r.content}</p>

              {/* HQ-SUGGEST-001: the question that was sent, and whatever came
                  back. Without this the card showed no sign a question had been
                  asked at all, so the same one could be asked twice and a reply
                  could sit unread. */}
              {(adminThread[r.id] ?? []).length > 0 && (
                <div className="mt-3 space-y-2 rounded-lg bg-offwhite p-3">
                  {(adminThread[r.id] ?? []).map(m => (
                    <div key={m.id} className={m.from_admin ? "" : "pl-5"}>
                      <div className="text-xs text-gray2">
                        {m.from_admin ? "You asked" : "They replied"} · {fmtDate(m.created_at)}
                      </div>
                      <p className="whitespace-pre-wrap text-sm">{m.body}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* ── WHAT THE WORK ACTUALLY PRODUCED ──────────────────────
                  Every line is a provider answer or a recorded artifact.
                  "Cole finished the change" appears only when GitHub confirmed
                  the pull request exists, and the card carries the link — so
                  nobody has to open another screen to find out whether anything
                  happened.

                  The test line is printed every time. Nothing builds Cole's
                  branch, and a card that went quiet about testing would be read
                  as "tested". */}
              {w && (
                <div className={`mt-3 rounded-lg border p-3 ${
                  w.stage === "SHIPPED" ? "border-good/40 bg-good/5"
                    : w.stage === "READY" ? "border-good/40 bg-good/5"
                      : w.stage === "FAILED" ? "border-bad/40 bg-bad/5"
                        : "border-line bg-offwhite"}`}>
                  <p className="text-sm font-medium text-ink">{w.headline}</p>
                  {w.detail && <p className="mt-1 text-sm text-ink-2">{w.detail}</p>}
                  {w.summary && <p className="mt-1 text-sm text-ink-2">{w.summary}</p>}

                  {/* ── THE ENGINEERING, UNDER A FOLD ──────────────────────
                      A pull request number, a branch name and a diff stat are
                      not the outcome of a business decision — they are how the
                      outcome was achieved. They stay reachable and stop being
                      the first thing read. When the change has shipped there is
                      nothing here to act on at all, which is the point. */}
                  {(w.stage === "SHIPPED" || w.stage === "READY" || w.stage === "SUPERSEDED")
                    && w.pull_request_number != null && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-xs text-gray2">Technical details</summary>
                      <p className="mt-1 text-xs text-gray2">
                        PR #{w.pull_request_number}
                        {w.branch ? ` · ${w.branch}` : ""}
                        {typeof w.files_changed === "number"
                          ? ` · ${w.files_changed} file${w.files_changed === 1 ? "" : "s"} changed` : ""}
                        {typeof w.additions === "number" ? ` · +${w.additions}/−${w.deletions}` : ""}
                      </p>
                      <ul className="mt-1 space-y-0.5">
                        {(w.checks ?? []).map(c => <li key={c} className="text-xs text-gray2">{c}</li>)}
                      </ul>
                      {w.pull_request_url && (
                        <a className="mt-1 inline-block text-xs text-teal underline"
                          href={w.pull_request_url} target="_blank" rel="noreferrer noopener">
                          Open on GitHub
                        </a>
                      )}
                    </details>
                  )}

                  {w.stage === "FAILED" && w.retry && (
                    <button className="btn btn-sm mt-2" disabled={busyId === r.id}
                      onClick={() => void resume(r, "Try again. If the same thing stops you, say what it was.")}>
                      Try again
                    </button>
                  )}
                  {/* A concluded answer can be right for the words and wrong for
                      the intent. The correction goes back through the same
                      pipeline, with his earlier conclusion attached. */}
                  {["ALREADY_DONE", "SHIPPED", "MERGED", "FAILED"].includes(w.stage) && !asking && (
                    <button className="btn-secondary btn-sm mt-2" disabled={busyId === r.id}
                      onClick={() => { setError(null); setText(""); setOpenInput({ id: r.id, decision: "clarify" }); }}>
                      That's not quite it — clarify
                    </button>
                  )}
                </div>
              )}
              {decided && r.admin_response && (
                <p className="mt-2 text-xs text-gray2">They were told: {r.admin_response}</p>
              )}

              {/* ── A JOB THAT STOPPED ON A QUESTION ─────────────────────
                  Nothing in HQ could move one of these: no tick claims that
                  status and no screen offered an action, so it waited for ever
                  and looked like Cole being slow. Answering it here carries the
                  work on. */}
              {w?.stage === "NEEDS_JEREMY" && (
                <div className="mt-3 rounded-lg border border-line bg-offwhite p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <button className="btn btn-sm" disabled={busyId === r.id}
                      onClick={() => void resume(r, "")}>
                      Just do the smallest version
                    </button>
                    <button className="btn-secondary btn-sm" disabled={busyId === r.id}
                      onClick={() => { setError(null); setText(""); setOpenInput({ id: r.id, decision: "resume" }); }}>
                      Answer him
                    </button>
                  </div>
                </div>
              )}

              {!decided && !asking && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button className="btn btn-sm" disabled={busyId === r.id}
                    onClick={() => void decide(r, "approve", "")}>
                    {busyId === r.id ? "…" : "Approve"}
                  </button>
                  <button className="btn-secondary btn-sm" disabled={busyId === r.id}
                    onClick={() => { setError(null); setText(""); setOpenInput({ id: r.id, decision: "deny" }); }}>
                    Deny
                  </button>
                  {!r.is_anonymous && r.author_id && (
                    <button className="btn-ghost btn-sm" disabled={busyId === r.id}
                      onClick={() => { setError(null); setText(""); setOpenInput({ id: r.id, decision: "ask" }); }}>
                      Ask a question
                    </button>
                  )}
                </div>
              )}

              {/* The one thing only he knows: why not, or what he needs to know. */}
              {asking && (
                <div className="mt-3 space-y-2">
                  <input
                    className="input" autoFocus value={text}
                    placeholder={openInput.decision === "deny"
                      ? "Why not? One line — they will read it."
                      : openInput.decision === "resume"
                        ? "Answer Cole in a line, and he carries on."
                        : openInput.decision === "clarify"
                          ? "What did they actually mean? Cole starts over from this."
                          : "What do you want to ask them?"}
                    onChange={e => setText(e.target.value)}
                    onKeyDown={e => {
                      if (e.key !== "Enter" || !text.trim()) return;
                      if (openInput.decision === "resume") void resume(r, text);
                      else if (openInput.decision === "clarify") void clarify(r, text);
                      else void decide(r, openInput.decision, text);
                    }}
                  />
                  <div className="flex flex-wrap items-center gap-2">
                    <button className="btn btn-sm" disabled={busyId === r.id || !text.trim()}
                      onClick={() => openInput.decision === "resume"
                        ? void resume(r, text)
                        : openInput.decision === "clarify"
                          ? void clarify(r, text)
                          : void decide(r, openInput.decision, text)}>
                      {openInput.decision === "deny" ? "Send decline"
                        : openInput.decision === "resume" ? "Send answer"
                          : openInput.decision === "clarify" ? "Send it back to Cole" : "Send question"}
                    </button>
                    <button className="btn-ghost btn-sm" disabled={busyId === r.id}
                      onClick={() => { setOpenInput(null); setText(""); }}>Cancel</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {inbox.rows.length === 0 && <div className="card py-10 text-center text-sm text-gray2">No suggestions.</div>}
      </div>
    </div>
  );
}
